import Aes from 'react-native-aes-crypto';

import {
  constantTimeEqualHex,
  PBKDF2_ITERATIONS,
} from './DocumentEncryptionV2';

/**
 * Step 5 — wrap a master recovery key under a Key Encryption Key (KEK)
 * derived from the user's secret code + per-user salt. The backend only ever
 * sees the wrapped bytes; it cannot unwrap without the secret code.
 *
 * Construction: AES-256-CBC + HMAC-SHA-256 (authenticated encryption).
 * Mirrors DocumentEncryptionV2's envelope so we reuse the same audited native
 * surface. Version tag is distinct (`w1cbc` vs v2's `v2`) so the two formats
 * cannot be confused at parse time.
 */

const WRAP_VERSION = 'w1cbc' as const;
const WRAP_AAD = 'mfk.wrap.v1';

// AES block size.
const IV_BYTES = 16;
const IV_HEX_LEN = IV_BYTES * 2;

// PBKDF2 output length (32-byte intermediate, then HKDF-expanded to 64).
const PBKDF2_OUT_BYTES = 32;
// react-native-aes-crypto's native pbkdf2 takes the output length in BITS,
// not bytes (see node_modules/react-native-aes-crypto/ios/Aes/lib/AesCrypt.m
// — `dataWithLength:length/8`). randomKey() in the same module uses bytes.
// Don't confuse the two — pass _BITS to pbkdf2, _BYTES to randomKey.
const PBKDF2_OUT_BITS = PBKDF2_OUT_BYTES * 8;
const ENC_KEY_HEX_LEN = 32 * 2;
const MAC_KEY_HEX_LEN = 32 * 2;
const COMBINED_KEY_HEX_LEN = ENC_KEY_HEX_LEN + MAC_KEY_HEX_LEN;

// HKDF-Expand "info" strings — distinct from any info string that
// DocumentEncryptionV2 or any future KDF in the app might adopt, so a KEK
// cannot accidentally equal a data-encryption key even when derived from the
// same secret code + salt.
const KEK_INFO_ENC = 'mfk.kek.v1.enc';
const KEK_INFO_MAC = 'mfk.kek.v1.mac';

export interface WrappedKeyEnvelope {
  version: typeof WRAP_VERSION;
  iv: string;         // hex, 32 chars
  ciphertext: string; // base64
  tag: string;        // hex, 64 chars (HMAC-SHA-256)
}

export class KeyWrappingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'KeyWrappingError';
  }
}

function splitKEK(combinedHex: string): { encKey: string; macKey: string } {
  if (
    typeof combinedHex !== 'string' ||
    combinedHex.length !== COMBINED_KEY_HEX_LEN
  ) {
    throw new KeyWrappingError(
      `KEK must be ${COMBINED_KEY_HEX_LEN} hex chars (got ${
        combinedHex?.length ?? 'none'
      })`,
    );
  }
  return {
    encKey: combinedHex.slice(0, ENC_KEY_HEX_LEN),
    macKey: combinedHex.slice(ENC_KEY_HEX_LEN),
  };
}

/**
 * Derive a 64-byte (128 hex char) KEK from the user's secret code and salt.
 * First runs PBKDF2-SHA256 at the OWASP 2024 iteration count to produce a
 * 32-byte intermediate; then HKDF-Expand's that into two independent halves
 * (enc + mac) using HMAC-SHA-256 with purpose-tagged info strings.
 *
 * The HKDF step is what keeps this KEK distinct from DocumentEncryptionV2's
 * data-encryption key even when both are derived from the same inputs.
 */
export async function deriveKEK(
  secretCode: string,
  salt: string,
  iterations: number = PBKDF2_ITERATIONS,
): Promise<string> {
  if (!secretCode) throw new KeyWrappingError('secret code is required');
  if (!salt) throw new KeyWrappingError('salt is required');
  if (iterations < 100000) {
    throw new KeyWrappingError(
      `iterations must be >= 100000 (got ${iterations})`,
    );
  }
  const ikm = await Aes.pbkdf2(
    secretCode,
    salt,
    iterations,
    PBKDF2_OUT_BITS,
    'sha256',
  );
  // HMAC-SHA256 with key=ikm, message=info-string produces 32 bytes each.
  // Concatenate for the 64-byte combined KEK.
  const encHalf = await Aes.hmac256(KEK_INFO_ENC, ikm);
  const macHalf = await Aes.hmac256(KEK_INFO_MAC, ikm);
  return encHalf + macHalf;
}

/**
 * Wrap a key (typically a 64-hex-char master recovery key) under a KEK.
 * AES-256-CBC with a fresh IV, authenticated by HMAC-SHA-256 over
 * (version || AAD || iv || ciphertext).
 */
export async function wrapKey(
  plaintextKey: string,
  kek: string,
): Promise<WrappedKeyEnvelope> {
  if (typeof plaintextKey !== 'string' || plaintextKey.length === 0) {
    throw new KeyWrappingError('plaintext key must be a non-empty string');
  }
  const { encKey, macKey } = splitKEK(kek);
  const iv = await Aes.randomKey(IV_BYTES);
  const ciphertext = await Aes.encrypt(plaintextKey, encKey, iv, 'aes-256-cbc');
  const tag = await Aes.hmac256(
    WRAP_VERSION + WRAP_AAD + iv + ciphertext,
    macKey,
  );
  return {
    version: WRAP_VERSION,
    iv,
    ciphertext,
    tag,
  };
}

/**
 * Unwrap a wrapped-key envelope. The HMAC tag is verified BEFORE decryption;
 * on any tag mismatch, version mismatch, or malformed envelope, throws
 * KeyWrappingError. There is no fallback to other formats.
 */
export async function unwrapKey(
  envelope: WrappedKeyEnvelope,
  kek: string,
): Promise<string> {
  if (!envelope || typeof envelope !== 'object') {
    throw new KeyWrappingError('envelope missing');
  }
  if (envelope.version !== WRAP_VERSION) {
    throw new KeyWrappingError(
      `unsupported version: ${String(envelope.version)}`,
    );
  }
  const { iv, ciphertext, tag } = envelope;
  if (
    typeof iv !== 'string' ||
    iv.length !== IV_HEX_LEN ||
    typeof ciphertext !== 'string' ||
    typeof tag !== 'string' ||
    tag.length !== 64
  ) {
    throw new KeyWrappingError('envelope malformed');
  }
  const { encKey, macKey } = splitKEK(kek);

  const expectedTag = await Aes.hmac256(
    WRAP_VERSION + WRAP_AAD + iv + ciphertext,
    macKey,
  );
  if (!constantTimeEqualHex(expectedTag, tag)) {
    throw new KeyWrappingError('authentication tag mismatch');
  }

  try {
    return await Aes.decrypt(ciphertext, encKey, iv, 'aes-256-cbc');
  } catch (err) {
    // Unreachable when HMAC matches, but keep error surface generic.
    throw new KeyWrappingError('ciphertext decryption failed');
  }
}

/**
 * A short, non-secret value the backend can store to prove the client knows
 * the KEK without the backend ever seeing the KEK or the secret code.
 *
 * Used to gate overwrite operations (change-secret-code). The backend
 * compares the submitted verifier against the stored one in constant time.
 *
 * This is NOT a password hash — it's already derived from one (PBKDF2 ran
 * inside deriveKEK). The purpose is domain separation: the verifier is
 * usable only for authorization checks and cannot be used to decrypt a
 * wrapped key even if leaked.
 */
export async function verifierFromKEK(kek: string): Promise<string> {
  const { macKey } = splitKEK(kek);
  return Aes.hmac256('mfk.kek.v1.verifier', macKey);
}

export function serializeWrappedKey(envelope: WrappedKeyEnvelope): string {
  return JSON.stringify(envelope);
}

export function parseWrappedKey(raw: string): WrappedKeyEnvelope {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new KeyWrappingError('envelope is not valid JSON');
  }
  if (!parsed || typeof parsed !== 'object') {
    throw new KeyWrappingError('envelope is not an object');
  }
  const { version, iv, ciphertext, tag } = parsed as Record<string, unknown>;
  if (version !== WRAP_VERSION) {
    throw new KeyWrappingError(`unsupported version: ${String(version)}`);
  }
  if (
    typeof iv !== 'string' ||
    typeof ciphertext !== 'string' ||
    typeof tag !== 'string'
  ) {
    throw new KeyWrappingError('envelope malformed');
  }
  return { version: WRAP_VERSION, iv, ciphertext, tag };
}
